// stores/vehicleStore.js
'use client';

import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';
import { normalizeApiResponse } from '../utils/response';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateVehicleTypeList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/vehicle-type/search');
};

export const useVehicleTypeList = (page, rowsPerPage, searchKey, searchValue, companyId = '') => {
  const start = page * rowsPerPage;
  const payload = useMemo(() => {
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .like(searchKey, searchValue, 'AND')
      .addJoin('company', ['name'])
      .addJoin('axle_type', ['name'])
      .setOrder(['name'], true);
    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companyId]);
  const key = useMemo(() => ['/vehicle-type/search', JSON.stringify(payload)], [payload]);
  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  const result = normalizeApiResponse(data, error, isLoading);
  return result;
};

export const getVehicleTypeById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));

    const payload = fb.build();
    const response = await fleetApi.post(`/vehicle-type/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    const result = Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
    return result;
  } catch (error) {
    console.error('Failed to fetch vehicle type by ID:', error);
    throw error;
  }
};

export const createVehicleType = async (formData) => {
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.post(`/vehicle-type/new${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateVehicleTypeList();
    return response.data;
  } catch (error) {
    console.error('Error creating vehicle type:', error);
    throw error;
  }
};

export const updateVehicleType = async (id, formData) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.put(`/vehicle-type/edit/${id}${getTzParam()}`, formData);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateVehicleTypeList();
    return response.data;
  } catch (error) {
    console.error('Error updating vehicle type:', error);
    throw error;
  }
};

export const deleteVehicleType = async (id) => {
  try {
    const response = await fleetApi.delete(`/vehicle-type/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateVehicleTypeList();
    return response.data;
  } catch (error) {
    console.error('Error deleting vehicle type:', error);
    throw error;
  }
};
